import React, { useEffect, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { I18nextProvider } from 'react-i18next';
// Build test - force new hash generation
import { useAppStore, useErrorStore } from './stores/appStore';
import { usePreventTranslation } from './hooks/usePreventTranslation';
import feeService from './services/feeService';
import apiService from './services/apiService';
import dialogService from './services/dialogService';
// import ApiDebugPanel from './components/ApiDebugPanel'; // Removed - no longer needed with Supabase
import safariServiceWorkerManager from './services/safariServiceWorkerManager';
import { initSyncManager } from './services/syncManager';
import { initOnlineStatusService } from './services/onlineStatusService';
import { supabase } from './lib/supabase';
import './i18n'; // Initialize i18n system (side effect import)
import i18n from './i18n';
import './index.css';

// Composants de base (à créer)
import AppLayout from './components/Layout/AppLayout';
import LoanConfirmPage from './pages/LoanConfirmPage';
// Page mission PUBLIQUE du module gestion-eau (hors garde d'auth)
const EauAccueilPage = React.lazy(() => import('./modules/gestion-eau/components/EauAccueilPage'));
// Résolveur de scan QR PUBLIC (applique la matrice de rôle / redirige vers mission)
const EauScanResolverPage = React.lazy(() => import('./modules/gestion-eau/components/EauScanResolverPage'));
// Vitrine PUBLIQUE d'invitation par jeton /i/:token (capture le jeton + CTA Google)
const EauVitrinePage = React.lazy(() => import('./modules/gestion-eau/components/EauVitrinePage'));
// NAVY ay (v3.81.0) : fiche PUBLIQUE d'un partenaire derrière son QR code /navy/p/:id
const NavyPublicPartnerPage = React.lazy(() => import('./modules/navy-ay/components/PublicPartnerPage'));
// NAVY ay (phase 3A) : page PUBLIQUE de téléchargement de l'appli Android /navy/app
const NavyAppDownloadPage = React.lazy(() => import('./modules/navy-ay/components/app/NavyAppDownloadPage'));
import ErrorBoundary from './components/ErrorBoundary';
import IOSInstallPrompt from './components/iOSInstallPrompt';
import UpdatePrompt from './components/UpdatePrompt';
import { ModuleSwitcherProvider } from './contexts/ModuleSwitcherContext';
// Construction POC Context - Mounted globally so Header can access ConstructionContext
import { ConstructionProvider } from './modules/construction-poc/context/ConstructionContext';
// Gestion Eau Context - Mounted globally (rôles admin/releveur/client, offline-first)
import { GestionEauProvider } from './modules/gestion-eau/context';

// Configuration React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

function App() {
  // Prevent automatic translation across entire application
  usePreventTranslation();
  
  const { setUser, setAuthenticated } = useAppStore();
  const { error, clearError } = useErrorStore();
  // const [isDebugPanelOpen, setIsDebugPanelOpen] = useState(false); // Removed - no longer needed with Supabase

  // Détection statut online/offline (events navigator + Page Visibility + ping serveur 2min)
  // Source de vérité : useAppStore.isOnline (+ useSyncStore.isOnline pour rétrocompat)
  useEffect(() => {
    const cleanup = initOnlineStatusService();
    return cleanup;
  }, []);

  // Initialisation de l'application + écoute session Supabase
  useEffect(() => {
    // ✅ Chargement du profil utilisateur depuis la table users
    const loadUserFromSupabase = async (userId: string) => {
      // Court-circuit offline : évite le timeout 5s inutile au démarrage.
      // Le profil reste celui persisté dans useAppStore (Zustand persist).
      // Quand la connexion revient, onAuthStateChange (TOKEN_REFRESHED/SIGNED_IN) rappellera
      // cette fonction avec réseau pour rafraîchir le profil.
      if (!navigator.onLine) {
        console.log('📡 Démarrage offline — profil chargé depuis le store local (pas d\'appel Supabase)');
        setAuthenticated(true);
        return;
      }
      try {
        // Timeout 5s : si la table users ne répond pas, on laisse quand même l'utilisateur accéder
        const queryPromise = supabase
          .from('users')
          .select('*')
          .eq('id', userId)
          .single();
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('DB timeout after 5s')), 5000)
        );

        const { data: userData, error } = await Promise.race([queryPromise, timeoutPromise]) as any;

        if (error || !userData) {
          console.warn('⚠️ Profil utilisateur introuvable dans users, session Supabase valide mais profil manquant');
          setAuthenticated(true);
          return;
        }

        setUser(userData as any);
        setAuthenticated(true);
        console.log('✅ Session Supabase restaurée pour:', userData.email);
      } catch (err) {
        console.error('❌ Erreur/timeout lors du chargement du profil:', err);
        // Session valide même si la requête DB échoue ou timeout → laisser accéder
        setAuthenticated(true);
      }
    };

    const initializeApp = async () => {
      try {
        // Initialiser le service de dialogues modernes
        dialogService.initialize();

        // Initialiser les services de base
        await feeService.initializeDefaultFees();

        // Initialiser le Service Worker adaptatif
        await safariServiceWorkerManager.initialize();

        // Initialiser le SyncManager (détecte Background Sync et active fallback si nécessaire)
        initSyncManager();

        // ✅ Vérifier la session Supabase au démarrage
        // NE PAS appeler setAuthenticated(false) si getSession() retourne null :
        // cela déclencherait un unmount/remount du Dashboard pendant le refresh token (bug boucle).
        // Le SIGNED_OUT event de onAuthStateChange gère la déconnexion réelle.
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          await loadUserFromSupabase(session.user.id);
        }

        // Tester la connexion API (mode offline)
        const isConnected = await apiService.testConnection();
        if (!isConnected) {
          console.warn('⚠️ Connexion API non disponible, mode hors ligne');
        }

      } catch (error) {
        console.error('❌ Erreur lors de l\'initialisation:', error);
      }
    };

    initializeApp();

    // ✅ onAuthStateChange gère les changements d'état d'authentification
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔐 Auth state change:', event);

      if (event === 'SIGNED_IN' && session?.user) {
        await loadUserFromSupabase(session.user.id);
      } else if (event === 'TOKEN_REFRESHED' && session?.user) {
        // Token refreshed silencieusement — s'assurer que le store reste cohérent
        const { setAuthenticated: setAuth } = useAppStore.getState();
        setAuth(true);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setAuthenticated(false);
        console.log('🚪 Session Supabase terminée — déconnexion du store');
      }
    });

    // Cleanup : désabonnement au démontage du composant
    return () => {
      subscription.unsubscribe();
    };
  }, [setUser, setAuthenticated]);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        {/* TODO Phase 3: Sync i18n.changeLanguage with appStore.language for VoiceInterface and PDF */}
        <I18nextProvider i18n={i18n}>
          <BrowserRouter>
            <ModuleSwitcherProvider>
              {/* ConstructionProvider mounted globally so Header (inside AppLayout) can access ConstructionContext */}
              <ConstructionProvider>
              {/* GestionEauProvider mounted globally so routes/nav can access roles */}
              <GestionEauProvider>
              <div className="min-h-screen bg-gray-50">
                <Routes>
                  <Route path="/loan-confirm/:token/*" element={<LoanConfirmPage />} />
                  {/* Page mission publique : accessible sans authentification */}
                  <Route
                    path="/gestion-eau/accueil"
                    element={
                      <Suspense fallback={<div className="min-h-screen" />}>
                        <EauAccueilPage />
                      </Suspense>
                    }
                  />
                  {/* Résolveur de scan QR : public (gère lui-même la matrice de rôle / mission) */}
                  <Route
                    path="/gestion-eau/scan"
                    element={
                      <Suspense fallback={<div className="min-h-screen" />}>
                        <EauScanResolverPage />
                      </Suspense>
                    }
                  />
                  {/* Vitrine d'invitation par jeton : publique (capte le jeton, CTA Google) */}
                  <Route
                    path="/i/:token"
                    element={
                      <Suspense fallback={<div className="min-h-screen" />}>
                        <EauVitrinePage />
                      </Suspense>
                    }
                  />
                  {/* NAVY ay : fiche publique du QR partenaire (sans connexion, données publiques seulement) */}
                  <Route
                    path="/navy/p/:id"
                    element={
                      <Suspense fallback={<div className="min-h-screen" />}>
                        <NavyPublicPartnerPage />
                      </Suspense>
                    }
                  />
                  {/* NAVY ay : téléchargement de l'appli Android (sans connexion) */}
                  <Route
                    path="/navy/app"
                    element={
                      <Suspense fallback={<div className="min-h-screen" />}>
                        <NavyAppDownloadPage />
                      </Suspense>
                    }
                  />
                  <Route path="*" element={<AppLayout />} />
                </Routes>
                <IOSInstallPrompt />
                <UpdatePrompt />
            
            {/* Toast Notifications */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 4000,
                  style: {
                    background: '#10B981',
                    color: '#fff',
                  },
                  iconTheme: {
                    primary: '#fff',
                    secondary: '#10B981',
                  },
                },
                error: {
                  duration: 5000,
                  style: {
                    background: '#EF4444',
                    color: '#fff',
                  },
                  iconTheme: {
                    primary: '#fff',
                    secondary: '#EF4444',
                  },
                },
              }}
            />
            
            {/* Bouton de débogage API - Removed - no longer needed with Supabase */}
            {/* <button
              onClick={() => setIsDebugPanelOpen(true)}
              className="fixed bottom-4 right-4 bg-blue-500 text-white p-3 rounded-full shadow-lg hover:bg-blue-600 transition-colors z-40"
              title="Configuration API"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
            
            <ApiDebugPanel
              isOpen={isDebugPanelOpen}
              onClose={() => setIsDebugPanelOpen(false)}
            /> */}
            
            {error && (
              <div className="fixed top-4 right-4 bg-red-500 text-white p-4 rounded-lg shadow-lg z-50">
                <div className="flex justify-between items-center">
                  <span>{String(error)}</span>
                  <button
                    onClick={clearError}
                    className="ml-4 text-white hover:text-gray-200"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}
              </div>
              </GestionEauProvider>
            </ConstructionProvider>
          </ModuleSwitcherProvider>
        </BrowserRouter>
        </I18nextProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;